import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface Option {
    value: string;
    label: string;
    disabled?: boolean;
}

interface ModernSelectProps {
    options: Option[];
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
}

const ModernSelect: React.FC<ModernSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select an option',
    disabled = false,
    className = '',
    size = 'md'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<Option | null>(
        options.find(option => option.value === value) || null
    );
    const selectRef = useRef<HTMLDivElement>(null);

    const sizeClasses = {
        sm: 'py-2 px-3 text-sm',
        md: 'py-3 px-4 text-base',
        lg: 'py-4 px-4 text-lg'
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const option = options.find(option => option.value === value);
        setSelectedOption(option || null);
    }, [value, options]);

    const handleSelect = (option: Option) => {
        if (option.disabled) return;

        setSelectedOption(option);
        onChange?.(option.value);
        setIsOpen(false);
    };

    const toggleOpen = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div ref={selectRef} className={`relative ${className}`}>
            {/* Trigger Button */}
            <button
                type="button"
                onClick={toggleOpen}
                disabled={disabled}
                className={`
                    w-full flex items-center justify-between
                    border-2 border-gray-200 dark:border-gray-600
                    rounded-xl
                    ${sizeClasses[size]}
                    ${disabled
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700'
                        : 'bg-white dark:bg-gray-800 hover:border-primary-300 dark:hover:border-primary-400 focus:border-primary-500 dark:focus:border-primary-500'
                    }
                    transition-all duration-200 ease-in-out
                    focus:outline-none focus:ring-2 focus:ring-primary-500/20
                `}
            >
                <span className={`${selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
                        }`}
                />
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl shadow-2xl max-h-60 overflow-auto">
                    {options.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => handleSelect(option)}
                            disabled={option.disabled}
                            className={`
                                w-full flex items-center justify-between px-4 py-3 text-left text-lg
                                ${option.disabled
                                    ? 'opacity-50 cursor-not-allowed text-gray-400'
                                    : 'hover:bg-primary-50 dark:hover:bg-primary-900/20 cursor-pointer'
                                }
                                ${selectedOption?.value === option.value
                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-500 dark:text-primary-400'
                                    : 'text-gray-700 dark:text-gray-300'
                                }
                                transition-colors duration-200 first:rounded-t-xl last:rounded-b-xl
                            `}
                        >
                            <span className="truncate">{option.label}</span>
                            {selectedOption?.value === option.value && (
                                <Check className="w-5 h-5 text-primary-500 dark:text-primary-400 flex-shrink-0" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ModernSelect;
